import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

/**
 * EmptyState — the ONLY empty-content pattern in the app.
 *
 * Every empty region MUST use this component so:
 *   - the user is never confronted with a raw empty box
 *   - the tone / spacing / typography stay consistent
 *   - a CTA is always one click away
 *
 * Props:
 *   illustration   ReactNode (SVG) — optional. Fallback: pass an Icon via `icon`.
 *   icon           Lucide icon component — used when no illustration.
 *   title          headline
 *   description    supporting sentence (educates the user)
 *   ctaLabel       CTA label (renders a link if `to`, or a button if `onClick`)
 *   to             react-router path
 *   onClick        button handler
 *   secondaryCta   { label, to?, onClick? } — optional
 *   testid
 *   tone           'default' | 'warning' — colours the illustration halo
 */
const EmptyState = ({
  illustration,
  icon: Icon,
  title,
  description,
  ctaLabel,
  to,
  onClick,
  secondaryCta,
  testid,
  tone = 'default',
}) => {
  const haloClass = tone === 'warning'
    ? 'bg-amber-500/8 border-amber-500/20'
    : 'bg-[#D4FF00]/6 border-[#D4FF00]/15';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className={`relative rounded-2xl border ${haloClass} overflow-hidden`}
      data-testid={testid}
    >
      {/* Ambient glow — signals warmth, not emptiness. */}
      <div
        className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl pointer-events-none opacity-40"
        style={{ background: tone === 'warning' ? '#F59E0B' : '#D4FF00' }}
      />
      <div className="relative flex flex-col md:flex-row items-center gap-8 md:gap-10 px-8 py-10 md:px-12">
        <div className="shrink-0" data-testid={testid ? `${testid}-art` : undefined}>
          {illustration ? illustration : (
            Icon && (
              <div className="w-24 h-24 rounded-2xl bg-[#D4FF00]/10 border border-[#D4FF00]/25 flex items-center justify-center">
                <Icon className="w-10 h-10 text-[#D4FF00]" strokeWidth={1.5} />
              </div>
            )
          )}
        </div>
        <div className="flex-1 text-center md:text-left">
          <h3 className="font-heading text-2xl md:text-3xl font-black tracking-tight mb-2" data-testid={testid ? `${testid}-title` : undefined}>
            {title}
          </h3>
          {description && (
            <p className="text-sm md:text-base text-zinc-400 max-w-md leading-relaxed" data-testid={testid ? `${testid}-desc` : undefined}>
              {description}
            </p>
          )}
          {(ctaLabel || secondaryCta) && (
            <div className="mt-6 flex flex-wrap gap-3 justify-center md:justify-start">
              {ctaLabel && (
                to ? (
                  <Link to={to} data-testid={testid ? `${testid}-cta` : undefined}>
                    <button className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#D4FF00] text-black font-semibold text-sm hover:bg-[#B3D600] transition-colors focus-ring">
                      {ctaLabel}
                    </button>
                  </Link>
                ) : (
                  <button
                    onClick={onClick}
                    data-testid={testid ? `${testid}-cta` : undefined}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#D4FF00] text-black font-semibold text-sm hover:bg-[#B3D600] transition-colors focus-ring"
                  >
                    {ctaLabel}
                  </button>
                )
              )}
              {secondaryCta && (
                secondaryCta.to ? (
                  <Link to={secondaryCta.to} data-testid={testid ? `${testid}-cta-secondary` : undefined}>
                    <button className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] text-sm text-zinc-200 transition-colors focus-ring">
                      {secondaryCta.label}
                    </button>
                  </Link>
                ) : (
                  <button
                    onClick={secondaryCta.onClick}
                    data-testid={testid ? `${testid}-cta-secondary` : undefined}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] text-sm text-zinc-200 transition-colors focus-ring"
                  >
                    {secondaryCta.label}
                  </button>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default EmptyState;
